/**
 * The cars — two lofted bodies, dressed, and wearing a loadout.
 *
 * ./car-body owns the SHELL: it turns a table of cross-section keyframes into a
 * painted loft with wheel wells cut into it, a glass canopy laid over the cabin
 * and a roof panel over that. This file owns everything that hangs off the
 * shell — wheels, splitter, diffuser, skirts, wing, mirrors, exhausts, lamps,
 * boost nozzles, topper, antenna — plus the per-frame behaviour (wheel spin,
 * steering, the supersonic tell) and the material assignment.
 *
 * The split is worth keeping. The shell is pure numeric geometry that can be
 * measured in a test; the dressing is art placed AGAINST that geometry, and it
 * asks the shell where its surface is (`bodyHalfWidthAt`, `sampleStation`)
 * rather than hard-coding coordinates. Move a station by a unit and the
 * headlights, mirrors and skirts move with it, instead of quietly detaching.
 *
 * LOCAL FRAME. `orientationToQuaternion` in ./convert puts the physics forward
 * axis on the mesh's -Z and the physics up axis on +Y, which is Three's own
 * object convention. So in this file:
 *
 *     -Z = forward (nose)      +Y = up      +X = the car's RIGHT
 *
 * Everything here is nonetheless AUTHORED in the car's physics frame (+x
 * forward, +y right, +z up), the frame src/physics/constants.ts quotes, and
 * converted at the point of use by `carPos` / `carBox`. Two conventions, one
 * conversion, stated once.
 *
 * ===========================================================================
 * LOADOUTS
 * ===========================================================================
 *
 * `buildCar` takes an optional `CarLivery` (see ./livery) and, given one, takes
 * its paint, finish, decal, number, flag, wheels, topper and antenna from it.
 * Given NONE it builds exactly the stock team car it always did — the
 * customization path is additive and the default path is not routed through it,
 * which is why the team-colour assertions in test/render/car-mesh still hold.
 *
 * ===========================================================================
 * SIX CARS
 * ===========================================================================
 *
 * Two things make six customized cars affordable, and neither is a level of
 * detail:
 *
 *   SHARING. Everything that depends only on the BODY — the shell, the canopy,
 *   the roof, the aero, the lamps — goes through `sharedGeometry`, so a 2v2 of
 *   Octanes uploads one shell between the four of them. Everything that depends
 *   on the LOADOUT is keyed on the loadout, so two players in the same colours
 *   share a texture and two in different colours do not.
 *
 *   MERGING. Parts that never move relative to each other and take the same
 *   material are baked into one geometry: three diffuser strakes are one draw,
 *   five emissive accents are one draw, a wheel's barrel and brake disc are one
 *   draw. `mergeGeometries` in ./car-body does the baking. It is worth roughly a
 *   quarter of the car's draw calls and costs nothing at all.
 */

import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  ExtrudeGeometry,
  Group,
  Mesh,
  Object3D,
  type Material,
  Shape,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import {
  POWERSLIDE_STEER_ANGLE_CURVE,
  STEER_ANGLE_CURVE,
} from '../physics/constants';
import { clamp, piecewiseLinear } from '../physics/math';
import {
  SECTION_INDEX,
  archHalfLength,
  buildBandGeometry,
  buildBodyGeometry,
  bodyHalfWidthAt,
  carBodySpec,
  mergeGeometries,
  sampleStation,
  wheelCentreY,
  wheelCentreZ,
  type CarBodyId,
} from './car-body';
import { makeCarMaterials, type CarMaterials } from './materials';
import {
  ANTENNA_GENERATORS,
  TOPPER_GENERATORS,
  acquireSkinTexture,
  antennaAnchor,
  attachmentColor,
  attachmentParts,
  boostStyleFor,
  finishFor,
  isStockSkin,
  materialKey,
  releaseSkinTexture,
  rimColor,
  rimGeometryFor,
  sharedGeometry,
  stockLivery,
  topperAnchor,
  trailStyleFor,
  tyreGeometryFor,
  type AttachmentMaterial,
  type BoostStyle,
  type CarLivery,
  type TrailStyle,
} from './livery';

export { CAR_ORIGIN_HEIGHT } from './car-body';
export type { CarBodyId } from './car-body';

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Wheel rotation for one frame, in radians, in the WHEEL MESH's local frame.
 *
 * The magnitude is the rolling-without-slipping rate, `omega = v / r`. The SIGN
 * is the part worth testing: the wheel spins about its local +X (the car's
 * right), and a positive rotation about +X carries +Y toward +Z — i.e. the top
 * of the wheel moves BACKWARD. Forward motion therefore needs a NEGATIVE
 * rotation, which is why this returns `-v * dt / r`. Get it wrong and the
 * wheels spin backwards, which nobody notices in code review and everybody
 * notices on screen.
 */
export const wheelSpinDelta = (forwardSpeed: number, radius: number, dt: number): number => {
  if (!(radius > 0) || !Number.isFinite(forwardSpeed) || !Number.isFinite(dt)) return 0;
  return (-forwardSpeed * dt) / radius;
};

/**
 * Visible front-wheel steer angle, radians, positive = left.
 *
 * Uses RocketSim's steer-angle curve and its powerslide blend in the documented
 * ORDER (blend the curves first, multiply by the input second — see
 * POWERSLIDE_STEER_ANGLE_CURVE). This is presentation only; the physics steers
 * by curvature, not by this angle. Matching the curve anyway means the wheels
 * visibly straighten as the car speeds up, which is what the driving model is
 * actually doing.
 */
export const visualSteerAngle = (speed: number, steer: number, powerslide = 0): number => {
  const v = clamp(Math.abs(speed), 0, 3000);
  let angle = piecewiseLinear(STEER_ANGLE_CURVE, v);
  const slide = clamp(powerslide, 0, 1);
  if (slide > 0) angle += (piecewiseLinear(POWERSLIDE_STEER_ANGLE_CURVE, v) - angle) * slide;
  return angle * clamp(steer, -1, 1);
};

// ---------------------------------------------------------------------------
// Car frame -> mesh frame
// ---------------------------------------------------------------------------

/** A car-frame point (x forward, y right, z up) as a mesh-frame vector. */
const carPos = (x: number, y: number, z: number): Vector3 => new Vector3(y, z, -x);

/** Bake a car-frame translation into a geometry, for a part about to be merged. */
const carTranslate = (geometry: BufferGeometry, x: number, y: number, z: number): BufferGeometry =>
  geometry.translate(y, z, -x);

/** A box given in car-frame extents: along the car, across it, and up. */
const carBox = (length: number, width: number, height: number): BoxGeometry =>
  new BoxGeometry(width, height, length);

/**
 * A flat plate given by its outline in (lateral, forward), extruded upward.
 *
 * Splitters, diffusers, wings and lamp lenses are all this shape, and all of
 * them need to TAPER to follow a body that is narrower at one end than the
 * other — which a box cannot do. Authored counter-clockwise in (lateral,
 * forward) so the extrusion's own normals come out facing up; the single
 * `rotateX` maps the shape's plane onto the car's ground plane and the
 * extrusion axis onto up.
 */
const carPlate = (outline: readonly (readonly [number, number])[], thickness: number): BufferGeometry => {
  const shape = new Shape();
  shape.moveTo(outline[0][0], outline[0][1]);
  for (let i = 1; i < outline.length; i++) shape.lineTo(outline[i][0], outline[i][1]);
  shape.closePath();
  const geometry = new ExtrudeGeometry(shape, {
    depth: thickness,
    bevelEnabled: false,
    curveSegments: 1,
  });
  // (x, y, z) -> (x, z, -y): shape-x stays lateral, the extrusion becomes up,
  // and shape-y becomes forward.
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(0, -thickness / 2, 0);
  return geometry;
};

/**
 * A tapered plate raked to bridge two underbody heights.
 *
 * The nose and tail of both cars rise toward their tips, so a flat splitter
 * hung under either one leaves a wedge of daylight between itself and the
 * bodywork. Raking it along the chord between the two heights it spans closes
 * that gap and, incidentally, is what a real splitter does.
 */
const rakedPlate = (
  xFront: number,
  xBack: number,
  zFront: number,
  zBack: number,
  halfFront: number,
  halfBack: number,
  thickness: number
): { geometry: BufferGeometry; position: Vector3; pitch: number } => {
  const dx = xFront - xBack;
  const dz = zFront - zBack;
  const chord = Math.hypot(dx, dz);
  const geometry = carPlate(
    [
      [-halfBack, -chord / 2],
      [halfBack, -chord / 2],
      [halfFront, chord / 2],
      [-halfFront, chord / 2],
    ],
    thickness
  );
  return {
    geometry,
    position: carPos((xFront + xBack) / 2, 0, (zFront + zBack) / 2),
    // Positive rotation about mesh X lifts the nose; see the file header.
    pitch: Math.atan2(dz, dx),
  };
};

// ---------------------------------------------------------------------------
// Wheels
// ---------------------------------------------------------------------------

export interface WheelVisual {
  /** Rotates about +Y for steering. Only the front pair is steered. */
  steer: Group;
  /** Rotates about +X for rolling. */
  spin: Group;
  radius: number;
  front: boolean;
}

/**
 * One wheel: a lathed tyre, a dark barrel and brake disc, and a rim face whose
 * shape comes from the loadout.
 *
 * The radius and width are the AXLE's, always — `RIM_GENERATORS` in ./livery
 * receives them and works in multiples of them, so no rim style can contradict
 * `src/physics/car-config.ts` about how big a wheel is. That is pinned by a
 * test, because a style that quietly grew a tyre would put the contact patch
 * under the floor and the car would look like it was sinking.
 */
const buildWheel = (
  livery: CarLivery,
  radius: number,
  width: number,
  front: boolean,
  materials: CarMaterials,
  side: 1 | -1
): WheelVisual => {
  const steer = new Group();
  const spin = new Group();
  steer.add(spin);

  const tyre = new Mesh(tyreGeometryFor(livery, radius, width), materials.tyre);
  tyre.castShadow = true;
  tyre.name = 'tyre';
  spin.add(tyre);

  // Barrel and brake disc are one draw: they never move relative to each other,
  // they take the same material, and neither is ever seen on its own.
  //
  // The disc is the depth cue a flat face has nothing of — with a dished rim in
  // front of it there is always a slice visible between two spokes, and that
  // slice is what tells the eye the wheel is turning.
  const dark = new Mesh(
    sharedGeometry(`rim-dark|${radius}|${width}|${side}`, () => {
      // Open-ended barrel: the caps would never be seen and the tyre's bead
      // covers the seam.
      const barrel = new CylinderGeometry(radius * 0.66, radius * 0.66, width * 0.84, 18, 1, true);
      barrel.rotateZ(Math.PI / 2);
      const disc = new CylinderGeometry(radius * 0.54, radius * 0.54, width * 0.12, 16);
      disc.rotateZ(Math.PI / 2);
      disc.translate(side * width * 0.06, 0, 0);
      return mergeGeometries([barrel, disc]);
    }),
    materials.rimDark
  );
  dark.name = 'rim-barrel';
  spin.add(dark);

  const rim = new Mesh(rimGeometryFor(livery, radius, width, front, side), materials.rim);
  rim.name = 'rim';
  rim.castShadow = true;
  spin.add(rim);

  return { steer, spin, radius, front };
};

// ---------------------------------------------------------------------------
// The car
// ---------------------------------------------------------------------------

export interface CarMesh {
  group: Group;
  team: 0 | 1;
  body: CarBodyId;
  /** What this car is wearing. Stock when `buildCar` was given no loadout. */
  livery: CarLivery;
  /** Boost flame and trail character, for `EffectsSystem.setCarStyle`. */
  boost: BoostStyle;
  trail: TrailStyle;
  wheels: WheelVisual[];
  /** Empty at the boost nozzle, in world space after `group.updateMatrixWorld`. */
  boostAnchor: Object3D;
  /** Drives the wheels from the car's motion. Call once per rendered frame. */
  updateWheels(forwardSpeed: number, steer: number, powerslide: number, dt: number): void;
  /** Team-coloured underglow / boost-charge tell. */
  setSupersonic(active: boolean): void;
  setVisible(visible: boolean): void;
  dispose(): void;
}

/**
 * Reference-counted car material sets.
 *
 * Kept here rather than in ./livery because the lifetime is a CAR's: acquired by
 * `buildCar` and released by `CarMesh.dispose`, so six identically-liveried cars
 * hold one set between them and the last one to go frees it. A plain cache would
 * either leak a set per garage preview or free one that five cars are drawing
 * with.
 */
const materialSets = new Map<string, { materials: CarMaterials; refs: number; livery: CarLivery }>();

const acquireMaterials = (livery: CarLivery): { key: string; materials: CarMaterials } => {
  const key = materialKey(livery);
  const existing = materialSets.get(key);
  if (existing) {
    existing.refs++;
    return { key, materials: existing.materials };
  }
  // A stock car takes no skin map at all: the material's own colour IS the team
  // colour, which keeps the default car byte-identical to what it was before
  // loadouts existed and keeps six stock cars off six megabytes of texture.
  const skin = isStockSkin(livery) ? null : acquireSkinTexture(livery);
  const materials = makeCarMaterials(livery.team, {
    skin,
    finish: finishFor(livery),
    paint: livery.primary,
    rim: rimColor(livery, 0xb9c4d6),
    attachment: livery.topper
      ? attachmentColor(livery, livery.topper)
      : livery.antenna
        ? attachmentColor(livery, livery.antenna)
        : undefined,
  });
  materialSets.set(key, { materials, refs: 1, livery });
  return { key, materials };
};

const releaseMaterials = (key: string): void => {
  const entry = materialSets.get(key);
  if (!entry) return;
  entry.refs--;
  if (entry.refs > 0) return;
  materialSets.delete(key);
  entry.materials.dispose();
  if (!isStockSkin(entry.livery)) releaseSkinTexture(entry.livery);
};

/** Live material sets. Six identically-liveried cars must report 1. */
export const carMaterialSetCount = (): number => materialSets.size;

export const buildCar = (
  team: 0 | 1,
  body: CarBodyId = 'octane',
  loadout?: CarLivery
): CarMesh => {
  const spec = carBodySpec(body);
  // The BODY argument wins: a networked match picks bodies from the room roster
  // and a livery carried over from another car must not silently redraw one.
  const livery: CarLivery = loadout ? { ...loadout, body: spec.id } : stockLivery(team, spec.id);
  const { key: materialsKey, materials } = acquireMaterials(livery);

  const group = new Group();
  group.name = `car-${spec.id}-${team === 0 ? 'blue' : 'orange'}`;

  const noseX = spec.stations[0].x;
  const tailX = spec.stations[spec.stations.length - 1].x;
  const at = (x: number) => sampleStation(spec.stations, x);
  const halfAt = (x: number, z: number): number => bodyHalfWidthAt(spec, x, z);
  /** Shared by body, since none of the dressing below depends on the loadout. */
  const shared = (part: string, build: () => BufferGeometry): BufferGeometry =>
    sharedGeometry(`${spec.id}|${part}`, build);

  /** Add a mesh at a car-frame point, named so tests and tooling can find it. */
  const add = (
    name: string,
    geometry: BufferGeometry,
    material: Material,
    x: number,
    y: number,
    z: number
  ): Mesh => {
    const mesh = new Mesh(geometry, material);
    mesh.name = name;
    mesh.position.copy(carPos(x, y, z));
    group.add(mesh);
    return mesh;
  };

  /** Add several already-positioned parts as ONE mesh. Positions are baked. */
  const addMerged = (name: string, part: string, material: Material, build: () => BufferGeometry[]): Mesh => {
    const mesh = new Mesh(shared(part, () => mergeGeometries(build())), material);
    mesh.name = name;
    group.add(mesh);
    return mesh;
  };

  // -- shell ----------------------------------------------------------------

  const shell = new Mesh(shared('shell', () => buildBodyGeometry(spec)), [
    materials.paint,
    materials.underbody,
  ]);
  shell.name = 'body';
  shell.castShadow = true;
  shell.receiveShadow = true;
  group.add(shell);

  // Glass over the whole cabin arc, painted panel laid back over the crown of
  // it: that pairing is what gives a windscreen, side glass and a backlight
  // without any of the three having to be positioned by hand.
  const canopy = new Mesh(
    shared('canopy', () => buildBandGeometry(spec, spec.cabin, SECTION_INDEX.upperStart, 0.4, 1.1)),
    materials.glass
  );
  canopy.name = 'canopy';
  group.add(canopy);

  // Body-coloured, not dark: the canopy underneath it is already near-black,
  // and a dark roof on top of dark glass turns the whole top of the car into
  // one unreadable slab.
  //
  // The 1.6 offset is not cosmetic either. The roof panel sits directly over
  // the canopy, the canopy is transmissive and therefore drawn in a later pass,
  // and a quarter of a unit of separation between the two is inside the depth
  // buffer's noise at this world scale — the glass wins and the roof
  // disappears. A clear unit and a half of air settles it.
  const roof = new Mesh(
    shared('roof', () => buildBandGeometry(spec, spec.roof, SECTION_INDEX.crownStart - 1, 1.6, 1.2)),
    materials.paint
  );
  roof.name = 'roof';
  roof.castShadow = true;
  group.add(roof);

  // -- aero -----------------------------------------------------------------

  const frontLipX = spec.front.mount.x + spec.front.radius * 0.95;
  const splitter = rakedPlate(
    noseX - 0.5,
    frontLipX,
    at(noseX - 4).bottom - 0.8,
    at(frontLipX).bottom - 1.4,
    halfAt(noseX - 4, at(noseX - 4).bottom + 2) + 0.5,
    halfAt(frontLipX, at(frontLipX).bottom + 2) + 1,
    2.2
  );
  const splitterMesh = new Mesh(
    sharedGeometry(`${spec.id}|splitter`, () => splitter.geometry),
    materials.trim
  );
  splitterMesh.name = 'splitter';
  splitterMesh.position.copy(splitter.position);
  splitterMesh.rotation.x = splitter.pitch;
  splitterMesh.castShadow = true;
  group.add(splitterMesh);

  const rearLipX = spec.back.mount.x - spec.back.radius * 0.95;
  const diffuser = rakedPlate(
    rearLipX,
    tailX + 0.5,
    at(rearLipX).bottom - 1.4,
    at(tailX + 4).bottom - 0.8,
    halfAt(rearLipX, at(rearLipX).bottom + 2) + 1.5,
    halfAt(tailX + 4, at(tailX + 4).bottom + 2) + 1,
    3
  );
  const diffuserMesh = new Mesh(
    sharedGeometry(`${spec.id}|diffuser`, () => diffuser.geometry),
    materials.trim
  );
  diffuserMesh.name = 'diffuser';
  diffuserMesh.position.copy(diffuser.position);
  diffuserMesh.rotation.x = diffuser.pitch;
  group.add(diffuserMesh);

  // Diffuser strakes. Three of them, because two reads as a gap and four is
  // noise at the size this is ever seen — and one draw, because they are three
  // boxes that never move relative to the car or to each other.
  const strakes = addMerged('strake', 'strakes', materials.underbody, () =>
    [-1, 0, 1].map((sy) =>
      carTranslate(
        carBox(14, 2, 5),
        tailX + 12,
        sy * halfAt(tailX + 12, at(tailX + 12).bottom + 2) * 0.5,
        at(tailX + 12).bottom - 0.5
      )
    )
  );
  strakes.castShadow = false;

  // Side skirts, spanning exactly the gap between the two wheel wells — the
  // arch's own reach, so a skirt ends where a well begins however the clearance
  // derivation moves.
  //
  // The SILL itself is in the loft now (see the station tables), which is the
  // only way to get a rocker that follows the body's own curvature and tucks
  // under it. What is left for this part to do is put a hard dark line along
  // the bottom of that rocker: a blade, one wheel-width of paint deep, that
  // stops the flank fading into its own shadow and reads from a chase camera as
  // the lowest edge of the car.
  const skirtFront = spec.front.mount.x - archHalfLength(spec, true);
  const skirtBack = spec.back.mount.x + archHalfLength(spec, false);
  const skirtX = (skirtFront + skirtBack) / 2;
  const skirtZ = at(skirtX).bottom + 1.4;
  for (const sy of [-1, 1] as const) {
    add(
      'skirt',
      shared('skirt', () => carBox(skirtFront - skirtBack, 3.4, 2.8)),
      materials.trim,
      skirtX,
      sy * (halfAt(skirtX, skirtZ) + 0.4),
      skirtZ
    ).castShadow = true;
  }

  // -- wing -----------------------------------------------------------------

  const wing = spec.wing;
  const bladeMesh = new Mesh(
    shared('wing', () =>
      carPlate(
        [
          [-wing.halfWidth, -wing.chord / 2],
          [wing.halfWidth, -wing.chord / 2],
          [wing.halfWidth * 0.88, wing.chord / 2],
          [-wing.halfWidth * 0.88, wing.chord / 2],
        ],
        2.6
      )
    ),
    materials.trim
  );
  bladeMesh.name = 'wing';
  bladeMesh.position.copy(carPos(wing.x, 0, wing.z));
  bladeMesh.rotation.x = 0.14;
  bladeMesh.castShadow = true;
  group.add(bladeMesh);

  addMerged('wing-stand', 'wing-stands', materials.trim, () =>
    [-1, 1].map((sy) =>
      carTranslate(
        carBox(wing.chord * 0.55, 3.4, wing.stand + 2),
        wing.x,
        sy * wing.halfWidth * 0.72,
        wing.z - wing.stand / 2 - 1
      )
    )
  );

  // -- mirrors --------------------------------------------------------------

  const mirrorX = spec.cabin.front - 1;
  const mirrorZ = at(mirrorX).belt + 1.5;
  const mirrorEdge = halfAt(mirrorX, mirrorZ);
  addMerged('mirror-stalk', 'mirror-stalks', materials.trim, () =>
    [-1, 1].map((sy) =>
      carTranslate(carBox(2, 3.6, 1.4), mirrorX, sy * (mirrorEdge + 1.8), mirrorZ)
    )
  );
  for (const sy of [-1, 1] as const) {
    add(
      'mirror',
      shared('mirror', () => carBox(2.6, 2.4, 3.6)),
      materials.trim,
      mirrorX - 0.4,
      sy * (mirrorEdge + 3.8),
      mirrorZ + 0.8
    );
  }

  // -- lamps ----------------------------------------------------------------

  // Lamps are LENSES, not blocks. A box lamp on a curved nose either floats or
  // has to be trimmed to fit, and it stuck out like a horn on the first pass;
  // a squashed ellipsoid sunk into the shell shows exactly the cap of itself
  // that is proud of the paint, whatever the surface is doing underneath.
  const lens = (radiusY: number, radiusZ: number, depth: number): SphereGeometry => {
    const geometry = new SphereGeometry(1, 12, 8);
    geometry.scale(radiusY, radiusZ, depth);
    return geometry;
  };

  // Set back from the tip by more than the lens is deep. Sitting it right on
  // the nose puts the front of the lens where the body has already tapered
  // away from it, and it bulges out of the corner like a growth.
  const lampX = noseX - 8;
  const lampZ = at(lampX).belt - 0.5;
  const lampEdge = halfAt(lampX, lampZ);
  for (const sy of [-1, 1] as const) {
    add(
      'headlight',
      shared('headlight', () => lens(7, 2.8, 4.5)),
      materials.headlight,
      lampX,
      sy * (lampEdge - 6),
      lampZ
    ).castShadow = false;
  }

  // Grille: a dark recess across the nose between the lamps.
  //
  // `carBox` takes FULL extents, so a width of `halfAt(...)` would be a part
  // half as wide as the body is — buried inside the paint and invisible. Every
  // width here is deliberately twice a half-width, less a margin.
  add(
    'grille',
    shared('grille', () => carBox(5, lampEdge * 1.55, 5)),
    materials.underbody,
    lampX - 1,
    0,
    lampZ - 2
  );

  const tailLampX = tailX + 3;
  const tailLampZ = at(tailLampX).belt + 2;
  const tailHalf = halfAt(tailLampX, tailLampZ);
  for (const sy of [-1, 1] as const) {
    add(
      'taillight',
      shared('taillight', () => lens(9, 3.4, 6)),
      materials.taillight,
      tailLampX,
      sy * (tailHalf - 8),
      tailLampZ
    ).castShadow = false;
  }
  // Full-width strip between them: the single clearest "that car is braking /
  // that car is there" cue from a chase camera two car-lengths back.
  //
  // A LENS again, not a box, and for a reason worth stating: the tail is a
  // taper, so its rearmost point is on the centreline and every box laid
  // across it is either buried in the middle or hanging off the corners. An
  // ellipsoid is furthest back exactly where the body is, so it emerges as an
  // even strip across the whole width.
  add(
    'taillight-bar',
    shared('taillight-bar', () => lens(tailHalf * 0.82, 1.7, 8)),
    materials.taillight,
    tailLampX - 1,
    0,
    tailLampZ
  );

  // Rear valance: a dark band under the lamps. Without it the tail is one
  // uninterrupted sheet of paint from the deck to the diffuser.
  add(
    'valance',
    shared('valance', () => lens(halfAt(tailLampX, tailLampZ - 8) * 0.85, 3.4, 8)),
    materials.underbody,
    tailLampX - 1,
    0,
    tailLampZ - 8
  );

  // -- exhausts and boost nozzles -------------------------------------------

  const exhaustZ = at(tailX + 6).bottom + 1.5;
  const exhaustSpan = halfAt(tailX + 6, exhaustZ);
  addMerged('exhaust', 'exhausts', materials.rim, () => {
    const parts: BufferGeometry[] = [];
    for (const sy of [-1, 1] as const) {
      for (let i = 0; i < spec.exhausts; i++) {
        const offset = spec.exhausts === 1 ? 0.62 : 0.48 + i * 0.22;
        const pipe = new CylinderGeometry(2.6, 2.9, 8, 10);
        pipe.rotateX(Math.PI / 2);
        parts.push(carTranslate(pipe, tailX + 4, sy * exhaustSpan * offset, exhaustZ));
      }
    }
    return parts;
  });

  const nozzleZ = at(tailX + 6).belt - 5;
  for (const sy of [-1, 1] as const) {
    const nozzle = new Mesh(
      shared('boost-nozzle', () => {
        const geometry = new CylinderGeometry(4.2, 5.4, 10, 12);
        geometry.rotateX(Math.PI / 2);
        return geometry;
      }),
      materials.trim
    );
    nozzle.name = 'boost-nozzle';
    nozzle.position.copy(carPos(tailX + 4, sy * 12, nozzleZ));
    group.add(nozzle);
  }

  // -- team accents ---------------------------------------------------------

  // Emissive team colour low on each flank, on the nose, and around the boost
  // nozzles. The paint is already team-coloured, but half the car is in shadow
  // half the time and a lit line is what survives that — and it stays the TEAM
  // colour whatever the loadout says, because it is the one thing on the car
  // that is information rather than decoration.
  //
  // Five parts, one draw.
  const accentZ = skirtZ + 5;
  addMerged('accent', 'accents', materials.accent, () => {
    const parts: BufferGeometry[] = [];
    for (const sy of [-1, 1] as const) {
      parts.push(
        carTranslate(
          carBox((skirtFront - skirtBack) * 0.94, 1.6, 2.6),
          skirtX,
          sy * (halfAt(skirtX, accentZ) + 0.4),
          accentZ
        )
      );
      const ring = new TorusGeometry(4.6, 0.8, 4, 12);
      parts.push(carTranslate(ring, tailX + 1.5, sy * 12, nozzleZ));
    }
    // And a chevron on the nose, which is the view an oncoming car gets.
    parts.push(carTranslate(carBox(3, lampEdge * 0.7, 2), lampX + 2, 0, lampZ + 3.5));
    return parts;
  });

  // -- hood detail ----------------------------------------------------------

  // One deliberate difference per car: the Octane wears a scoop, the Dominus
  // gets a pair of long vents. Both sit on the hood, read at a glance, and are
  // the cheapest way to make the two cars' top-down views differ too.
  const hoodX = (spec.front.mount.x + spec.cabin.front) / 2;
  const hoodTop = at(hoodX).top;
  if (spec.id === 'octane') {
    // Tapered, so it sits on a hood that is itself narrowing toward the nose.
    add(
      'hood-scoop',
      shared('hood-scoop', () =>
        carPlate(
          [
            [-10, -7],
            [10, -7],
            [7, 7],
            [-7, 7],
          ],
          3.4
        )
      ),
      materials.trim,
      hoodX,
      0,
      hoodTop - 0.2
    );
    add(
      'hood-intake',
      shared('hood-intake', () => carBox(2.2, 11, 2.6)),
      materials.underbody,
      hoodX + 6.6,
      0,
      hoodTop + 0.3
    );
  } else {
    addMerged('hood-vent', 'hood-vents', materials.underbody, () =>
      [-1, 1].map((sy) => carTranslate(carBox(22, 7, 1.6), hoodX, sy * 13, hoodTop + 0.3))
    );
  }

  // -- topper and antenna ---------------------------------------------------

  // The two slots that change the SILHOUETTE, which is why neither can be a
  // decal. Both are planted from the body's own station table (see
  // `topperAnchor` / `antennaAnchor` in ./livery), so a roof lowered by a unit
  // carries its topper down with it.
  const attachmentMaterial = (role: AttachmentMaterial): Material =>
    role === 'painted'
      ? materials.attachment
      : role === 'metal'
        ? materials.rim
        : role === 'glow'
          ? materials.accent
          : materials.trim;

  const attach = (
    name: string,
    ref: CarLivery['topper'],
    registry: typeof TOPPER_GENERATORS,
    anchor: ReturnType<typeof topperAnchor>
  ): void => {
    if (!ref) return;
    const parts = attachmentParts(registry, ref, anchor, `${name}|${spec.id}`);
    if (!parts) return;
    const mount = new Group();
    mount.name = name;
    mount.position.copy(carPos(anchor.x, anchor.y, anchor.z));
    for (const part of parts) {
      const mesh = new Mesh(part.geometry, attachmentMaterial(part.material));
      mesh.name = `${name}-${part.material}`;
      mesh.castShadow = true;
      mount.add(mesh);
    }
    group.add(mount);
  };

  attach('topper', livery.topper, TOPPER_GENERATORS, topperAnchor(spec.id));
  attach('antenna', livery.antenna, ANTENNA_GENERATORS, antennaAnchor(spec.id));

  const boostAnchor = new Object3D();
  boostAnchor.position.copy(carPos(tailX - 5, 0, nozzleZ));
  group.add(boostAnchor);

  // Supersonic tell: an emissive shell that switches on with the flag. Cheaper
  // and far more readable at distance than a particle-only cue.
  const aura = new Mesh(
    sharedGeometry('aura', () => new SphereGeometry(78, 16, 12)),
    materials.aura
  );
  aura.name = 'supersonic-aura';
  aura.scale.set(0.62, 0.36, 1.05);
  aura.position.copy(carPos(0, 0, at(0).belt));
  aura.visible = false;
  group.add(aura);

  // -- wheels ---------------------------------------------------------------

  const wheels: WheelVisual[] = [];
  for (const front of [true, false] as const) {
    const axle = front ? spec.front : spec.back;
    for (const sy of [1, -1] as const) {
      const wheel = buildWheel(livery, axle.radius, axle.width, front, materials, sy);
      wheel.steer.name = `wheel-${front ? 'front' : 'back'}-${sy > 0 ? 'right' : 'left'}`;
      wheel.steer.position.copy(
        carPos(axle.mount.x, sy * wheelCentreY(spec, front), wheelCentreZ(axle.radius))
      );
      group.add(wheel.steer);
      wheels.push(wheel);
    }
  }

  return {
    group,
    team,
    body: spec.id,
    livery,
    boost: boostStyleFor(livery),
    trail: trailStyleFor(livery),
    wheels,
    boostAnchor,
    updateWheels(forwardSpeed: number, steer: number, powerslide: number, dt: number): void {
      const angle = visualSteerAngle(forwardSpeed, steer, powerslide);
      for (const wheel of wheels) {
        wheel.spin.rotation.x += wheelSpinDelta(forwardSpeed, wheel.radius, dt);
        if (wheel.front) wheel.steer.rotation.y = angle;
      }
    },
    setSupersonic(active: boolean): void {
      aura.visible = active;
    },
    setVisible(visible: boolean): void {
      group.visible = visible;
    },
    dispose(): void {
      // SHARED geometry is deliberately not freed: it belongs to the body or to
      // a wheel recipe, not to this car, and dropping it because one of six cars
      // went away would make the other five re-upload a shell.
      group.traverse((o: Object3D) => {
        const mesh = o as Mesh;
        if (mesh.geometry && mesh.geometry.userData.shared !== true) mesh.geometry.dispose();
      });
      releaseMaterials(materialsKey);
    },
  };
};

// ---------------------------------------------------------------------------
// Measurement
// ---------------------------------------------------------------------------

/**
 * What one car costs the renderer.
 *
 * `draws` counts what `WebGLRenderer` would actually issue: one call per mesh,
 * except for a mesh with an ARRAY of materials, which issues one per group. The
 * distinction matters and is easy to get wrong — a `BoxGeometry` carries six
 * groups whether or not anything uses them, so counting groups unconditionally
 * would report a car six times more expensive than it is.
 *
 * Measured rather than argued, because the whole justification for merging parts
 * is a number and a number nobody measures is a claim.
 */
export interface CarRenderCost {
  draws: number;
  triangles: number;
  /** Geometries this car shares with every other car of the same recipe. */
  sharedGeometries: number;
}

export const carRenderCost = (car: CarMesh): CarRenderCost => {
  let draws = 0;
  let triangles = 0;
  let shared = 0;
  car.group.traverse((o: Object3D) => {
    const mesh = o as Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    draws += Array.isArray(mesh.material) ? Math.max(1, mesh.geometry.groups.length) : 1;
    const index = mesh.geometry.getIndex();
    const position = mesh.geometry.getAttribute('position');
    triangles += Math.round((index ? index.count : position ? position.count : 0) / 3);
    if (mesh.geometry.userData.shared === true) shared++;
  });
  return { draws, triangles, sharedGeometries: shared };
};

/**
 * World position of a car's rear boost nozzle. Requires the car's world matrix
 * to be up to date, which it is by the time effects run in the render pass.
 */
export const boostAnchorWorld = (car: CarMesh, out = new Vector3()): Vector3 =>
  car.boostAnchor.getWorldPosition(out);
